import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, X } from "lucide-react";
import { api, formatApiError, resolveMediaUrl } from "@/lib/api";

/**
 * Multi-file logo uploader for settings lists (client logos, etc).
 * Uploads sequentially to avoid PHP multi-upload limits.
 */
const BulkLogoUpload = ({
  items = [],
  onChange,
  folder = "clients",
  label = "Logo klien",
  maxFiles = 200,
}) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // { done, total }

  const newId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${folder}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/media/upload`, fd, {
      params: { folder },
      headers: { "Content-Type": "multipart/form-data" },
    });
    const name = (file.name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
    return {
      id: newId(),
      name,
      image: resolveMediaUrl(data.url),
    };
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.type.startsWith("image/"));
    if (!files.length) {
      toast.error("Pilih file gambar");
      return;
    }
    if (items.length + files.length > maxFiles) {
      toast.error(`Maksimal ${maxFiles} logo`);
      return;
    }

    const oversized = files.filter((f) => f.size > 8 * 1024 * 1024);
    if (oversized.length) {
      toast.error(`${oversized.length} file melebihi 8 MB dan dilewati`);
    }
    const queue = files.filter((f) => f.size <= 8 * 1024 * 1024);
    if (!queue.length) return;

    setProgress({ done: 0, total: queue.length });
    const uploaded = [];
    let failed = 0;

    for (let i = 0; i < queue.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        uploaded.push(await uploadOne(queue[i]));
      } catch (e) {
        failed += 1;
        console.error(e);
      }
      setProgress({ done: i + 1, total: queue.length });
    }

    if (uploaded.length) {
      onChange([...items, ...uploaded]);
      toast.success(`${uploaded.length} logo berhasil diunggah`);
    }
    if (failed) {
      toast.error(`${failed} file gagal diunggah`);
    }
    setProgress(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeOne = (id) => onChange(items.filter((x) => x.id !== id));
  const clearAll = () => {
    if (!items.length) return;
    if (!window.confirm(`Hapus semua ${items.length} logo?`)) return;
    onChange([]);
  };

  const busy = !!progress;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-sm font-semibold text-zinc-800">{label}</div>
          <p className="text-xs text-zinc-500 mt-0.5">
            {items.length} logo · pilih banyak file sekaligus (max {maxFiles})
          </p>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={14} /> Hapus semua
            </button>
          )}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid="bulk-upload-clients"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-maroon-800 text-white text-sm font-semibold hover:bg-maroon-900 disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {busy ? `Mengunggah ${progress.done}/${progress.total}` : "Upload massal"}
          </button>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {busy && (
        <div className="mb-4 h-2 rounded-full bg-zinc-100 overflow-hidden">
          <div
            className="h-full bg-maroon-700 transition-all"
            style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
          />
        </div>
      )}

      {items.length === 0 ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50 py-12 flex flex-col items-center gap-2 text-zinc-500 hover:border-maroon-400 hover:text-maroon-700"
        >
          <Upload size={22} />
          <span className="text-sm font-semibold">Klik untuk pilih banyak logo</span>
          <span className="text-xs text-zinc-400">PNG, JPG, WEBP · max 8 MB per file</span>
        </button>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 max-h-[420px] overflow-y-auto p-1">
          {items.map((c) => (
            <div
              key={c.id}
              className="relative group aspect-square rounded-xl border border-zinc-200 bg-white p-2 flex items-center justify-center"
              title={c.name || ""}
            >
              <img
                src={resolveMediaUrl(c.image)}
                alt={c.name || "Logo"}
                className="max-h-full max-w-full object-contain"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => removeOne(c.id)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 text-red-600 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                aria-label="Hapus"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkLogoUpload;
